import { Injectable } from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {classToPlain, plainToClass} from 'class-transformer';
import {db} from "../../dexie/db";

import { SOPutAssessmentSummary } from './so-put-assessment-summary';
import { SOGetAssessmentSummary } from './so-get-assessment-summary';
import { SOPostAssessmentSummary } from './so-post-assessment-summary';
import { SODeleteAssessmentSummary } from './so-delete-assessment-summary';
import {SORespAssessmentSummary} from './so-resp-assessment-summary';
import {SOPluralAssessmentSummary} from './so-plural-assessment-summary';

import {MMEntityService} from 'lionheartland';
import {EntityCoreProtocol} from 'lionheartland';
import {MMEntityServiceBase} from 'lionheartland';
import {HttpHeaders} from "@angular/common/http";

export class EOAssessmentSummaryService extends MMEntityServiceBase implements MMEntityService {

  constructor(
    public entityCore: EntityCoreProtocol,
  ) {
    super('assessment_summary', entityCore);
  }

  /** GET AssessmentSummary by id. Will 404 if id not found */
  get(request: SOGetAssessmentSummary): Observable<SOPluralAssessmentSummary> {
    return this.httpClient.get<SOPluralAssessmentSummary>(this.makeGetUri(request), this.httpOptions).pipe(
      map(resp => plainToClass(SOPluralAssessmentSummary, resp)),
      tap(async (response: SOPluralAssessmentSummary) => {
        this.log(`fetched AssessmentSummary`);
        // After a successful server response, update IndexedDB
        const activities = response.toAssessmentSummarys(); // Convert your response to an array of AssessmentSummary instances
        const promises = activities.map(assessmentSummary => db.assessmentSummarys.put(assessmentSummary));
        await Promise.all(promises); // Wait for all the IndexedDB update operations to complete
      }),
      catchError(this.handleError<SOPluralAssessmentSummary>(`get AssessmentSummary`))
    );
  }

    /** GETCACHE for initial display with CacheQueryProtocol */
    getCache(cacheQuery: any): Observable<SOPluralAssessmentSummary> {
        console.log("GETCACHE", cacheQuery);

        if (cacheQuery.filterKey === 'assessmentSummary' && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on parent and organization")
            return from(db.assessmentSummarys.where('parentUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(assessmentSummarysArray => this.processAssessmentSummarys(assessmentSummarysArray)),
                catchError(error => this.handleQueryError(error))
            );
        }
        else if (cacheQuery.filterKey + "s" == "assessment" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.assessmentSummarys.where('assessmentUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(assessmentSummarysArray => this.processAssessmentSummarys(assessmentSummarysArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "project" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.assessmentSummarys.where('projectUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(assessmentSummarysArray => this.processAssessmentSummarys(assessmentSummarysArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "organization" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.assessmentSummarys.where('organizationUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(assessmentSummarysArray => this.processAssessmentSummarys(assessmentSummarysArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterOrganization || cacheQuery.organization.uid)
        {
            console.log("tripped organization for cache query")
            if ( cacheQuery?.filterOrganization != null )
            {
                console.log("cache filtering based on organization CQP", cacheQuery)
                return from(db.assessmentSummarys.where('organizationUid').equals(cacheQuery.filterOrganization).toArray()).pipe(
                    map(assessmentSummarysArray => this.processAssessmentSummarys(assessmentSummarysArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else if ( cacheQuery?.organization?.uid != null )
            {
                console.log("UID:", cacheQuery.organization.uid)
                console.log("cache filtering based on organization SOGet")
                return from(db.assessmentSummarys.where('organizationUid').equals(cacheQuery.organization.uid).toArray()).pipe(
                    map(assessmentSummarysArray => this.processAssessmentSummarys(assessmentSummarysArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else
            {
                console.log("null organization")
            }
        }
        else
        {
            console.log("cache no filter")
            return from(db.assessmentSummarys.toArray()).pipe(
                map((assessmentSummarysArray) => {
                    // Convert the array of plain objects to an array of SORespAssessmentSummary instances
                    const sorAssessmentSummarys = assessmentSummarysArray.map(req => plainToClass(SORespAssessmentSummary, req));
                    // Create and return a new instance of SOPluralAssessmentSummary
                    const pluralAssessmentSummary = new SOPluralAssessmentSummary();
                    pluralAssessmentSummary.objects = sorAssessmentSummarys;
                    pluralAssessmentSummary.nextOffset = null; // Assuming there is no pagination in cache
                    return pluralAssessmentSummary;
                }),
                catchError(error => this.handleQueryError(error))
            );
        }
    }

    private processAssessmentSummarys(assessmentSummarysArray) {
        const sorAssessmentSummarys = assessmentSummarysArray.map(req => plainToClass(SORespAssessmentSummary, req));
        const pluralAssessmentSummary = new SOPluralAssessmentSummary();
        pluralAssessmentSummary.objects = sorAssessmentSummarys;
        pluralAssessmentSummary.nextOffset = null;
        return pluralAssessmentSummary;
    }

    private handleQueryError(error) {
        console.error('Error in query:', error);
        return throwError(error); // Return an observable error
    }

post(request: SOPostAssessmentSummary): Observable<SORespAssessmentSummary> {
  return this.httpClient.post<SORespAssessmentSummary>(this.coreUrl + this.route, request, this.httpOptions).pipe(
    map(resp => plainToClass(SORespAssessmentSummary, resp)),

    tap(async (response: SORespAssessmentSummary) => {
      this.log(`added AssessmentSummary w/ id=${response.uid}`);
      // After a successful server response, update IndexedDB
      console.log("here", response);
      console.log("here request", request);
      await db.assessmentSummarys.add(response.toAssessmentSummary());
    }),
    catchError(this.handleError<SORespAssessmentSummary>('AssessmentSummary post'))
  );
}


  put(request: SOPutAssessmentSummary): Observable<SORespAssessmentSummary> {
    const uid = request.uid;
    const uri = `${this.coreUrl}${this.route}/${uid}`;
    return this.httpClient.put<SORespAssessmentSummary>(uri, request, this.httpOptions).pipe(
      map(resp => plainToClass(SORespAssessmentSummary, resp)),
      tap(async (response: SORespAssessmentSummary) => {
        this.log(`edited AssessmentSummary w/ uid=${uid}`);
        // After a successful server response, update IndexedDB
        await db.assessmentSummarys.put(response.toAssessmentSummary());
      }),
      catchError(this.handleError<SORespAssessmentSummary>('AssessmentSummary put'))
    );
  }


  delete(request: SODeleteAssessmentSummary): Observable<SORespAssessmentSummary> {
    return this.httpClient.delete<SORespAssessmentSummary>(this.makeDeleteUri(request), this.httpOptions).pipe(
      tap(async _ => {
        this.log(`deleted AssessmentSummary uid=${request.uid}`);
        // After a successful server response, update IndexedDB
        await db.assessmentSummarys.delete(request.uid);
      }),
      catchError(this.handleError<SORespAssessmentSummary>('AssessmentSummary delete'))
    );
  }


}
